/**
 * TreeNavigator — nested Layers panel for the new `blob.tree` schema.
 * Delegates recursive rendering to `TreeNodeRow` (co-locating a self-
 * referencing JSX component in this file trips a babel/traverse stack
 * overflow — see TreeNodeRow.jsx header).
 */
import { useState } from "react";
import { Layers, Plus } from "lucide-react";
import {
  makeSection, makeRow, makeColumn, makeWidget, updateNode, removeNode,
  duplicateNode, insertChild, reorderSibling, isSection, isRow, isColumn,
} from "@/lib/visualBuilder/treeSchema";
import TreeNodeRow from "./TreeNodeRow";

export default function TreeNavigator({ tree, selection, onSelect, onChange }) {
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [dragId, setDragId] = useState(null);

  const setTree = (fn) => onChange({ tree: fn(tree || []) });

  const addToNode = (parent) => {
    if (isSection(parent)) setTree((t) => insertChild(t, parent.id, makeRow()));
    else if (isRow(parent)) setTree((t) => insertChild(t, parent.id, makeColumn(6)));
    else if (isColumn(parent)) setTree((t) => insertChild(t, parent.id, makeWidget("text")));
  };
  const addTopSection = () => setTree((t) => [...t, makeSection()]);
  const dup = (id) => setTree((t) => duplicateNode(t, id));
  const del = (id) => setTree((t) => removeNode(t, id));
  const hide = (id, cur) => setTree((t) => updateNode(t, id, { hidden: !cur }));
  const lock = (id, cur) => setTree((t) => updateNode(t, id, { locked: !cur }));
  const rename = (id) => {
    setTree((t) => updateNode(t, id, { label: editVal }));
    setEditing(null);
  };
  const reorder = (id, idx) => setTree((t) => reorderSibling(t, id, idx));

  const rows = tree || [];

  return (
    <div className="border-t border-slate-100 bg-slate-50/50" data-testid="vb-tree-nav">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-white">
        <Layers className="w-3.5 h-3.5 text-slate-500" />
        <div className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 flex-1">Tree · Sections</div>
        <button
          type="button" onClick={addTopSection}
          className="w-6 h-6 grid place-items-center rounded bg-[#E32636] text-white hover:opacity-90"
          data-testid="vb-tree-add-section"
          title="Add Section"
        ><Plus className="w-3 h-3" /></button>
      </div>

      <div className="max-h-[42vh] overflow-y-auto py-1">
        {rows.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-[11px] text-slate-500 mb-2">No sections yet.</div>
            <button
              type="button" onClick={addTopSection}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-[#E32636] hover:bg-[#c11e2b] text-white text-[11px] font-semibold"
              data-testid="vb-tree-empty-add"
            >
              <Plus className="w-3 h-3" /> Add your first section
            </button>
          </div>
        ) : rows.map((sec) => (
          <TreeNodeRow
            key={sec.id}
            node={sec}
            depth={0}
            selection={selection}
            editing={editing} setEditing={setEditing}
            editVal={editVal} setEditVal={setEditVal}
            onSelect={onSelect}
            onAddChild={addToNode}
            onDup={dup} onDel={del} onHide={hide} onLock={lock} onRename={rename}
            dragId={dragId} setDragId={setDragId}
            onReorder={reorder}
            allNodes={rows}
          />
        ))}
      </div>
    </div>
  );
}
